import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { User } from '../generated/gql-types';
import { availableWorkersActions } from './availableWorkers.action';
import { storeAvailableWorkersInWorkerStore } from './availableWorkers.effect';

interface AvailableWorkersState {
  isLoading: boolean;
  availableWorkers: Record<string, string[]>; // Map of jobId -> userIds[]
  error: string | null;
  fetchUsersAvailableForJob: (jobId: string) => Promise<string[]>;
  getUsersAvailableForJob: (jobId: string) => string[];
}

export const useAvailableWorkersStore = create<AvailableWorkersState>()(
  persist(
    (set, get) => ({
      //   state
      availableWorkers: {},
      isLoading: false,
      error: null,

      //   actions
      fetchUsersAvailableForJob: async (jobId: string) => {
        try {
          set({ isLoading: true });
          const availableWorkers =
            await availableWorkersActions.fetchUsersAvailableForJob(jobId);

          if (availableWorkers) {
            storeAvailableWorkersInWorkerStore(availableWorkers);
            const availableWorkerIds = availableWorkers.map(
              (worker) => worker.id,
            );
            set((state) => ({
              availableWorkers: {
                ...state.availableWorkers,
                [jobId]: availableWorkerIds,
              },
            }));
            return availableWorkerIds;
          } else {
            set((state) => ({
              availableWorkers: { ...state.availableWorkers, [jobId]: [] },
              error: 'No users available for job',
            }));
            return [];
          }
        } catch (error) {
          console.error('Error fetching users available for job:', error);
          set({ error: error as string });
          return [];
        } finally {
          set({ isLoading: false });
        }
      },
      getUsersAvailableForJob: (jobId: string) => {
        return get().availableWorkers[jobId] || [];
      },
    }),
    {
      name: 'available-workers-storage',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({ availableWorkers: state.availableWorkers }),
    },
  ),
);
